import { Editor, NgxEditorModule, toHTML, Toolbar } from 'ngx-editor';
import {
    Component,
    computed,
    forwardRef,
    HostBinding,
    input,
    model,
    OnDestroy,
    OnInit,
    output,
    ViewEncapsulation
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    UntypedFormControl,
    Validator
} from '@angular/forms';
import { AixRichTextAddLinkComponent } from './aix-rich-text-add-link.component';

@Component({
    selector: 'aix-rich-text',
    templateUrl: './aix-rich-text.component.html',
    styleUrls: ['./aix-rich-text.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => AixRichTextComponent)
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: AixRichTextComponent
        }
    ],
    standalone: true,
    imports: [NgxEditorModule, AixRichTextAddLinkComponent]
})
export class AixRichTextComponent implements ControlValueAccessor, OnInit, Validator, OnDestroy {
    _propagateChanges: (value: string) => void = () => ({});
    _propagateTouches: () => void = () => ({});

    height = input<number>();
    isRequired = input<boolean>(false);
    isDisabled = input<boolean>(false);
    isDirty = model<boolean>(false);
    isValid = model<boolean>(false);
    value = model<string>('');
    rawValue = computed(() => this.value().replace(/(<([^>]+)>|&nbsp;)/gi, ''));

    valueChanges = output<string>();
    setDirty = output();

    editor: Editor;

    @HostBinding('attr.aix-control')
    aixControl: string;

    toolbar: Toolbar = [
        ['bold', 'italic'],
        ['underline', 'strike'],
        // ['code'],
        ['ordered_list', 'bullet_list'],
        // ['link'],
        // ['text_color', 'background_color'],
        ['align_left', 'align_center', 'align_right', 'align_justify']
    ];

    constructor() {}

    ngOnInit(): void {
        this.editor = new Editor();

        this.editor.valueChanges.subscribe(r => {
            if (!this.isDirty()) {
                this.setDirty.emit();
                this.isDirty.set(true);
            }

            const html = toHTML(r).replace(/<p>\s*<\/p>/g, '<p>&nbsp;</p>');
            this.value.set(html);
            this.valueChanges.emit(html);
            this._propagateChanges(html);
        });
    }

    validate({ value }: UntypedFormControl) {
        const rawValue = value?.replace(/(<([^>]+)>|&nbsp;)/gi, '').trim();
        return rawValue ? null : { invalid: true };
    }

    writeValue(value: any) {
        const html = value
            ? value.startsWith('<')
                ? value
                : value
                      .split(/\r?\n/)
                      .map((line: any) => `<p>${line || '&nbsp;'}</p>`)
                      .join('')
            : '';

        this.editor.setContent(html);
        this.value.set(html);
    }

    registerOnChange(fn: (value: string) => void) {
        this._propagateChanges = fn;
    }

    registerOnTouched(fn: () => void) {
        this._propagateTouches = fn;
    }

    ngOnDestroy(): void {
        // make sure to destroy the editor
        this.editor.destroy();
    }
}
